import { useState, useCallback } from 'react'
import './ImportWizard.css'
import Step1_SelectTarget from './steps/Step1_SelectTarget'
import Step2_UploadFile from './steps/Step2_UploadFile'
import Step3_ColumnMapping from './steps/Step3_ColumnMapping'
import Step4_ValidateConfirm from './steps/Step4_ValidateConfirm'
import type { ImportTarget } from './steps/Step1_SelectTarget'
import type { ParsedFile } from './steps/Step2_UploadFile'

interface ImportWizardProps {
  companyId: number
}

const STEPS = [
  { num: 1, label: 'Select Target' },
  { num: 2, label: 'Upload File' },
  { num: 3, label: 'Map Columns' },
  { num: 4, label: 'Review & Import' },
]

function ImportWizard({ companyId }: ImportWizardProps) {
  const [step, setStep] = useState(1)
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null)
  const [targets, setTargets] = useState<ImportTarget[]>([])
  const [parsedFile, setParsedFile] = useState<ParsedFile | null>(null)
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  // Step 1 -> 2
  const handleSelectTarget = useCallback((targetKey: string, allTargets: ImportTarget[]) => {
    setSelectedTarget(targetKey)
    setTargets(allTargets)
    setStep(2)
  }, [])

  // Step 2 -> 3
  const handleFileParsed = useCallback((result: ParsedFile, _uploadedFile: File) => {
    setParsedFile(result)
    setStep(3)
  }, [])

  // Step 3 -> 4
  const handleMapped = useCallback((mappings: Record<string, string>) => {
    setColumnMappings(mappings)
    setStep(4)
  }, [])

  // Step 4 -> reset (import more)
  const handleComplete = useCallback(() => {
    setStep(1)
    setSelectedTarget(null)
    setParsedFile(null)
    setColumnMappings({})
    setError(null)
  }, [])

  // Back handlers
  const goToStep1 = useCallback(() => {
    setStep(1)
    setParsedFile(null)
    setColumnMappings({})
  }, [])

  const goToStep2 = useCallback(() => {
    setStep(2)
    setColumnMappings({})
  }, [])

  const goToStep3 = useCallback(() => {
    setStep(3)
  }, [])

  return (
    <div className="import-wizard">
      {/* Step indicator bar */}
      <div className="iw-step-bar">
        {STEPS.map((s, i) => {
          const isActive = s.num === step
          const isDone = s.num < step
          const cls = `iw-step${isActive ? ' iw-step--active' : ''}${isDone ? ' iw-step--done' : ''}`
          return (
            <div key={s.num} style={{ display: 'contents' }}>
              {i > 0 && <div className="iw-step-connector" />}
              <div className={cls}>
                <div className="iw-step-circle">
                  {isDone ? '\u2713' : s.num}
                </div>
                <span className="iw-step-label">{s.label}</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ padding: '0 24px', paddingTop: 16 }}>
          <div className="iw-error">
            {error}
            <button className="iw-error-dismiss" onClick={() => setError(null)}>x</button>
          </div>
        </div>
      )}

      {/* Active step */}
      <div className="iw-content">
        {step === 1 && (
          <Step1_SelectTarget onSelect={handleSelectTarget} />
        )}
        {step === 2 && (
          <Step2_UploadFile
            companyId={companyId}
            onParsed={handleFileParsed}
            onBack={goToStep1}
          />
        )}
        {step === 3 && parsedFile && selectedTarget && (
          <Step3_ColumnMapping
            parsedFile={parsedFile}
            target={selectedTarget}
            targets={targets}
            onMapped={handleMapped}
            onBack={goToStep2}
          />
        )}
        {step === 4 && parsedFile && selectedTarget && (
          <Step4_ValidateConfirm
            companyId={companyId}
            target={selectedTarget}
            parsedFile={parsedFile}
            columnMappings={columnMappings}
            onComplete={handleComplete}
            onBack={goToStep3}
          />
        )}
      </div>
    </div>
  )
}

export default ImportWizard
